import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Eye,
  AlertTriangle,
  RefreshCw,
  ArrowRight,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import StatusBadge from '../../components/StatusBadge';
import { agrupacionesAPI, describirError } from '../../services/api';
import { formatoMoneda, formatoTelefono, limpiarNombreGrupo } from '../../utils/finanzas';
import '../../styles/Pages.css';
import '../../styles/DataTable.css';
import '../../styles/Agrupaciones.css';

export default function Grupos() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState('TODOS');
  const [conSaldo, setConSaldo] = useState('TODOS');
  const [ordenar, setOrdenar] = useState('FOLIO_ASC');

  const cargarGrupos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        tipoAgrupacion: 'GRUPAL',
        page: pagination.page,
        limit: pagination.limit,
        ordenar
      };
      if (q.trim()) params.q = q.trim();
      if (estado !== 'TODOS') params.estado = estado;
      if (conSaldo === 'CON_SALDO') params.conSaldo = true;
      if (conSaldo === 'SIN_SALDO') params.conSaldo = false;

      const res = await agrupacionesAPI.listar(params);
      setData(res.data.data || []);
      setKpis(res.data.kpis || null);
      setPagination(prev => ({
        ...prev,
        total: res.data.pagination?.total || 0,
        totalPages: res.data.pagination?.totalPages || 1
      }));
    } catch (err) {
      setError(describirError(err).mensaje);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, q, estado, conSaldo, ordenar]);

  useEffect(() => {
    cargarGrupos();
  }, [cargarGrupos]);

  return (
    <div className="cartera-container">
      {/* Encabezado */}
      <div className="cartera-header">
        <div>
          <div className="cartera-badge-env">
            <span className="cartera-badge-dot" />
            <span>MODALIDAD GRUPAL</span>
          </div>
          <h1 className="cartera-title">Grupos de Crédito</h1>
          <p className="cartera-subtitle">
            Catálogo de grupos solidarios con saldo vivo y métricas canónicas consolidadas.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={cargarGrupos}
            disabled={loading}
            title="Actualizar datos"
          >
            <RefreshCw size={15} className={loading ? 'spin' : ''} />
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="cartera-kpi-grid">
        <div className="cartera-kpi-card">
          <div className="cartera-kpi-label">GRUPOS REGISTRADOS</div>
          <div className="cartera-kpi-value font-mono">
            {kpis ? kpis.totalEntidades : '—'}
          </div>
          <div className="cartera-kpi-sub">Entidades en catálogo</div>
        </div>

        <div className="cartera-kpi-card">
          <div className="cartera-kpi-label">CRÉDITOS VINCULADOS</div>
          <div className="cartera-kpi-value font-mono">
            {kpis ? kpis.creditosVinculados : '—'}
          </div>
          <div className="cartera-kpi-sub">
            Con saldo: <strong>{kpis ? kpis.creditosConSaldo : '—'}</strong>
          </div>
        </div>

        <div className="cartera-kpi-card">
          <div className="cartera-kpi-label">SALDO ACTUAL</div>
          <div className="cartera-kpi-value font-mono text-primary">
            {kpis ? formatoMoneda(kpis.saldoActual) : '—'}
          </div>
          <div className="cartera-kpi-sub">Canónico consolidado</div>
        </div>

        <div className="cartera-kpi-card">
          <div className="cartera-kpi-label">SALDO VENCIDO</div>
          <div className="cartera-kpi-value font-mono text-danger">
            {kpis ? formatoMoneda(kpis.saldoVencido) : '—'}
          </div>
          <div className="cartera-kpi-sub">
            Por vencer: <strong>{kpis ? formatoMoneda(kpis.saldoPorVencer) : '—'}</strong>
          </div>
        </div>

        {/* Tarjeta Excepción de No Vinculados */}
        <div 
          className="cartera-kpi-card" 
          style={{ 
            backgroundColor: '#fffbeb', 
            borderColor: '#fde68a',
            cursor: 'pointer' 
          }}
          onClick={() => navigate('/grupos/sin-vinculacion')}
          title="Ver créditos grupales históricos sin vinculación"
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="cartera-kpi-label" style={{ color: '#b45309' }}>SIN VINCULACIÓN</div>
            <ArrowRight size={14} color="#b45309" />
          </div>
          <div className="cartera-kpi-value font-mono" style={{ color: '#b45309' }}>
            {kpis ? kpis.sinVinculacionHistorica : '—'}
          </div>
          <div className="cartera-kpi-sub" style={{ color: '#92400e' }}>
            Excepciones históricas &rarr;
          </div>
        </div>
      </div>

      {/* Barra de Filtros */}
      <div className="cartera-filters-card">
        <div className="cartera-filters-row">
          <div className="cartera-search-box">
            <Search size={16} className="cartera-search-icon" />
            <input
              type="text"
              className="cartera-search-input"
              placeholder="Buscar por folio, nombre de grupo o responsable..."
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            />
          </div>

          <div className="cartera-filter-item">
            <span className="cartera-filter-label">Estado:</span>
            <select
              className="cartera-select"
              value={estado}
              onChange={(e) => {
                setEstado(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="TODOS">Todos los estados</option>
              <option value="ACTIVA">Activa</option>
              <option value="INACTIVA">Inactiva</option>
            </select>
          </div>

          <div className="cartera-filter-item">
            <span className="cartera-filter-label">Universo:</span>
            <select
              className="cartera-select"
              value={conSaldo}
              onChange={(e) => {
                setConSaldo(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="TODOS">Todos los grupos</option>
              <option value="CON_SALDO">Con saldo vivo</option>
              <option value="SIN_SALDO">Saldo $0.00</option>
            </select>
          </div>

          <div className="cartera-filter-item">
            <span className="cartera-filter-label">Ordenar:</span>
            <select
              className="cartera-select"
              value={ordenar}
              onChange={(e) => setOrdenar(e.target.value)}
            >
              <option value="FOLIO_ASC">Folio (A &rarr; Z)</option>
              <option value="FOLIO_DESC">Folio (Z &rarr; A)</option>
              <option value="NOMBRE_ASC">Nombre (A &rarr; Z)</option>
              <option value="SALDO_DESC">Mayor Saldo</option>
              <option value="VENCIDO_DESC">Mayor Vencido</option>
              <option value="CREDITOS_DESC">Más Créditos</option>
              <option value="ATRASO_DESC">Mayor Atraso</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="cartera-error-card">
          <AlertTriangle size={18} />
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-secondary" onClick={cargarGrupos}>
            Reintentar
          </button>
        </div>
      )}

      {/* Tabla */}
      <div className="table-responsive">
        <table className="data-table">
          <thead>
            <tr>
              <th>CÓDIGO</th>
              <th>NOMBRE DEL GRUPO</th>
              <th>RESPONSABLE</th>
              <th className="text-center">CRÉDITOS</th>
              <th className="text-center">CON SALDO</th>
              <th className="text-center">INTEGRANTES</th>
              <th className="text-right">SALDO ACTUAL</th>
              <th className="text-right">SALDO VENCIDO</th>
              <th className="text-right">SALDO POR VENCER</th>
              <th>ESTADO</th>
              <th className="text-right">ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            {loading && data.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center py-4">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    <RefreshCw size={16} className="spin" />
                    <span>Cargando grupos de crédito...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center py-4 text-muted">
                  No se encontraron grupos que coincidan con los filtros activos.
                </td>
              </tr>
            ) : (
              data.map((grupo) => {
                const infoNombre = limpiarNombreGrupo(grupo.nombre);
                return (
                  <tr
                    key={grupo.id}
                    className="fila-clicable"
                    onClick={() => navigate(`/grupos/${grupo.id}`)}
                    title={`Ver expediente del grupo ${infoNombre.nombre}`}
                  >
                    <td>
                      <span className="font-mono text-bold creditos-folio-cell">
                        {grupo.folio}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                        <span className="font-semibold text-primary">
                          {infoNombre.nombre}
                        </span>
                        {infoNombre.badge && (
                          <span style={{
                            fontSize: '0.7rem',
                            padding: '0.1rem 0.4rem',
                            borderRadius: '0.25rem',
                            backgroundColor: '#f1f5f9',
                            color: '#475569',
                            fontWeight: '600',
                            border: '1px solid #e2e8f0',
                            whiteSpace: 'nowrap'
                          }}>
                            {infoNombre.badge}
                          </span>
                        )}
                      </div>
                    </td>
                  <td>
                    {grupo.responsable ? (
                      <div>
                        <div>{grupo.responsable.nombre}</div>
                        {grupo.responsable.telefono && (
                          <div className="text-xs text-muted">{formatoTelefono(grupo.responsable.telefono)}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted text-xs">Sin responsable asignado</span>
                    )}
                  </td>
                  <td className="text-center font-mono">
                    {grupo.creditosVinculados}
                  </td>
                  <td className="text-center font-mono">
                    {grupo.creditosConSaldo}
                  </td>
                  <td className="text-center font-mono">
                    {grupo.totalIntegrantes}
                  </td>
                  <td className="text-right font-mono font-semibold">
                    {formatoMoneda(grupo.saldoActual)}
                  </td>
                  <td className="text-right font-mono font-semibold text-danger">
                    {formatoMoneda(grupo.saldoVencido)}
                  </td>
                  <td className="text-right font-mono text-muted">
                    {formatoMoneda(grupo.saldoPorVencer)}
                  </td>
                  <td>
                    <StatusBadge value={grupo.estado} />
                  </td>
                  <td className="text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-icon"
                      onClick={() => navigate(`/grupos/${grupo.id}`)}
                      title="Ver expediente del grupo"
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              );
            })
          )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="cartera-pagination">
        <span className="text-sm text-muted">
          Mostrando {data.length} de {pagination.total} grupos registrados
        </span>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={pagination.page <= 1 || loading}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            <ChevronLeft size={14} />
            <span>Anterior</span>
          </button>
          <span className="text-sm font-mono px-2">
            Pág. {pagination.page} de {pagination.totalPages}
          </span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={pagination.page >= pagination.totalPages || loading}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            <span>Siguiente</span>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
